import { CheckCircle as CheckIcon, Sms as SmsIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  cancelSMS,
  confirmPhone,
  getSMSStatus,
  subscribeSMS,
  verifyPhone,
} from "../api/alerts";

const TIERS = [
  {
    key: "starter",
    nameKey: "sms.starter",
    priceKey: "sms.starterPrice",
    limitKey: "sms.starterLimit",
  },
  {
    key: "pro",
    nameKey: "sms.pro",
    priceKey: "sms.proPrice",
    limitKey: "sms.proLimit",
  },
  {
    key: "unlimited",
    nameKey: "sms.unlimited",
    priceKey: "sms.unlimitedPrice",
    limitKey: "sms.unlimitedLimit",
  },
];

export default function SMSSubscription() {
  const { t } = useTranslation("alerts");
  const [smsStatus, setSmsStatus] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      setSmsStatus(await getSMSStatus());
    } catch {
      /* ignore */
    }
  };

  const showSnack = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });
  const isActive = smsStatus && smsStatus.status === "active";

  const handleSubscribe = async (tier) => {
    try {
      const data = await subscribeSMS(tier);
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (e) {
      showSnack(e.message, "error");
    }
  };

  const handleCancel = async () => {
    if (!window.confirm(t("sms.cancelConfirm"))) return;
    try {
      await cancelSMS();
      loadStatus();
      showSnack(t("common.success"));
    } catch (e) {
      showSnack(e.message, "error");
    }
  };

  const handleSendCode = async () => {
    try {
      await verifyPhone(phoneNumber);
      setCodeSent(true);
      showSnack(t("common.success"));
    } catch (e) {
      showSnack(e.message, "error");
    }
  };

  const handleVerify = async () => {
    try {
      await confirmPhone(phoneNumber, verifyCode);
      loadStatus();
      showSnack(t("sms.verified"));
      setCodeSent(false);
      setVerifyCode("");
    } catch (e) {
      showSnack(e.message, "error");
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 1 }}>
        {t("sms.title")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t("sms.description")}
      </Typography>

      {/* Current Subscription Status */}
      {isActive && (
        <Card sx={{ mb: 3, border: "2px solid", borderColor: "success.main" }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <CheckIcon color="success" />
              <Typography variant="h6">
                {t("sms.currentPlan")}: {smsStatus.tier?.toUpperCase()}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t("sms.usage")}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <LinearProgress
                variant="determinate"
                value={
                  smsStatus.monthly_sms_limit > 0
                    ? Math.min(
                        (smsStatus.monthly_sms_used /
                          smsStatus.monthly_sms_limit) *
                          100,
                        100,
                      )
                    : 0
                }
                sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
              />
              <Typography variant="body2">
                {smsStatus.monthly_sms_used} /{" "}
                {smsStatus.monthly_sms_limit === 999999
                  ? "Unlimited"
                  : smsStatus.monthly_sms_limit}
              </Typography>
            </Box>
            {smsStatus.phone_verified && (
              <Chip
                icon={<CheckIcon />}
                label={`${t("sms.verified")}: ${smsStatus.phone_number}`}
                color="success"
                size="small"
                sx={{ mt: 2 }}
              />
            )}
            <Box sx={{ mt: 2 }}>
              <Button color="error" variant="outlined" onClick={handleCancel}>
                {t("sms.cancelSubscription")}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Pricing Cards */}
      {!isActive && (
        <Box sx={{ display: "flex", gap: 3, mb: 4, flexWrap: "wrap" }}>
          {TIERS.map((tier) => (
            <Card
              key={tier.key}
              sx={{ flex: "1 1 250px", maxWidth: 320, textAlign: "center" }}
            >
              <CardContent>
                <SmsIcon sx={{ fontSize: 40, color: "primary.main", mb: 1 }} />
                <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                  {t(tier.nameKey)}
                </Typography>
                <Typography
                  variant="h4"
                  color="primary"
                  sx={{ my: 2, fontWeight: "bold" }}
                >
                  {t(tier.priceKey)}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  {t(tier.limitKey)}
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => handleSubscribe(tier.key)}
                >
                  {t("sms.subscribe")}
                </Button>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Phone Verification */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t("sms.phoneVerification")}
          </Typography>
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end", mb: 2 }}>
            <TextField
              label={t("sms.phoneNumber")}
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="+1234567890"
              size="small"
            />
            <Button
              variant="outlined"
              onClick={handleSendCode}
              disabled={!phoneNumber}
            >
              {t("sms.sendCode")}
            </Button>
          </Box>
          {codeSent && (
            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end" }}>
              <TextField
                label={t("sms.verificationCode")}
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value)}
                size="small"
              />
              <Button
                variant="contained"
                onClick={handleVerify}
                disabled={!verifyCode}
              >
                {t("sms.verify")}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
